import * as React from 'react';

import { TAdBannerEventPrefix } from '../../../types/events';
import { getEventQueue } from '../../../utils/eventQueue';

type TUseAdBannerMicrofrontendEventsParams = {
  /** Тот же placeId, что передан в `<Microfrontend parameters={...} />`. */
  placeId: string;
  isReady: boolean;
  onLoad?(): void;
  onStub?(): void;
  onError?(): void;
  onClose?(): void;
};

/**
 * Подписывает хост на lifecycle-события микрофронтенда ad-banner-frontend.
 * Топики строятся из placeId: `ad-banner-frontend-reporting.v1.<placeId>.ad-banner-<event>`.
 */
export function useAdBannerMicrofrontendEvents({
  placeId,
  isReady,
  onLoad,
  onStub,
  onError,
  onClose,
}: TUseAdBannerMicrofrontendEventsParams): void {
  React.useEffect(() => {
    if (!isReady || (!onLoad && !onStub && !onError && !onClose)) {
      return undefined;
    }

    const eventQueue = getEventQueue();
    const prefix: TAdBannerEventPrefix = `ad-banner-frontend-reporting.v1.${placeId}.ad-banner-`;
    const loadEventName = `${prefix}load` as const;
    const stubEventName = `${prefix}stub` as const;
    const errorEventName = `${prefix}error` as const;
    const closeEventName = `${prefix}close` as const;

    if (onLoad) {
      eventQueue.on(loadEventName, onLoad);
    }

    if (onStub) {
      eventQueue.on(stubEventName, onStub);
    }

    if (onError) {
      eventQueue.on(errorEventName, onError);
    }

    if (onClose) {
      eventQueue.on(closeEventName, onClose);
    }

    return () => {
      if (onLoad) {
        eventQueue.off(loadEventName, onLoad);
      }

      if (onStub) {
        eventQueue.off(stubEventName, onStub);
      }

      if (onError) {
        eventQueue.off(errorEventName, onError);
      }

      if (onClose) {
        eventQueue.off(closeEventName, onClose);
      }
    };
  }, [placeId, isReady, onLoad, onStub, onError, onClose]);
}
